import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { listMyCompetitions } from "../../../../../lib/clientCompetitions";
import { formatDeadlineCompact } from "../../../../../lib/datetime";
import { listDeliverables } from "../../../../../lib/deliverables";
import { supabase } from "../../../../../lib/supabase";
import { resolveActiveCaseId, writeActiveCaseId } from "../../../../../lib/useActiveCase";
import type { ClientCompetitionListRow, Deliverable } from "../../../../../types/database";
import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const OFF_BLACK = THEME.surfaceBg;
const W = "rgba(255,255,255,1)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const SAGE = COLORS.SAGE.hex;
const RED = THEME.destructive;

type DeliverableWithStats = Deliverable & {
  submitted_team_count: number;
  total_team_count: number;
};

async function loadDeliverableStats(competitionId: string): Promise<DeliverableWithStats[]> {
  const items = await listDeliverables(competitionId);
  if (!items.length) return [];

  const [{ data: teamRows }, { data: subRows }] = await Promise.all([
    supabase.from("teams").select("id").eq("competition_id", competitionId),
    supabase.from("submissions").select("deliverable_id, team_id").eq("competition_id", competitionId),
  ]);

  const totalTeams = (teamRows ?? []).length;

  const submittedByDeliverable = new Map<string, Set<string>>();
  for (const s of subRows ?? []) {
    const did = s.deliverable_id as string;
    const tid = s.team_id as string;
    if (!submittedByDeliverable.has(did)) submittedByDeliverable.set(did, new Set<string>());
    submittedByDeliverable.get(did)?.add(tid);
  }

  return items.map((d) => ({
    ...d,
    submitted_team_count: submittedByDeliverable.get(d.id)?.size ?? 0,
    total_team_count: totalTeams,
  }));
}

function fileTypeLabel(t: string): string {
  switch (t) {
    case "pdf":
      return "PDF";
    case "docx":
      return "Word";
    case "xlsx":
      return "Excel";
    case "pptx":
      return "Slides";
    case "any":
      return "Any";
    default:
      return t.toUpperCase();
  }
}

export default function ClientCaseDeliverablesScreen() {
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const urlCaseId = Array.isArray(params.id) ? params.id[0] : params.id;

  const [rows, setRows] = useState<ClientCompetitionListRow[]>([]);
  const [items, setItems] = useState<DeliverableWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listMyCompetitions();
      setRows(list);

      const fromUrl = urlCaseId && list.some((r) => r.id === urlCaseId) ? urlCaseId : null;
      const resolved = fromUrl ?? resolveActiveCaseId(list);
      if (resolved) {
        writeActiveCaseId(resolved);
        const stats = await loadDeliverableStats(resolved);
        setItems(stats);
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [urlCaseId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const activeCase = urlCaseId ? rows.find((r) => r.id === urlCaseId) ?? null : null;
  const showInvalid = !loading && !!urlCaseId && rows.length > 0 && !activeCase;
  if (showInvalid) {
    queueMicrotask(() => router.replace("/client" as Href));
  }

  const now = Date.now();

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 32, paddingTop: 28, paddingBottom: 60 }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Case deliverables
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "flex-end", gap: 16, marginTop: 8 }}>
          <Text
            style={{
              fontFamily: FONTS.display,
              fontSize: 32,
              lineHeight: 36,
              color: W,
              flex: 1,
            }}
            numberOfLines={1}
          >
            {activeCase?.name ?? "Deliverables"}
          </Text>
          <Pressable
            onPress={() => {
              if (urlCaseId) router.push(`/client/competition/${urlCaseId}` as Href);
            }}
            style={[
              {
                paddingHorizontal: 14,
                paddingVertical: 9,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.5)",
                backgroundColor: "rgba(181,135,58,0.08)",
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Open editor →
            </Text>
          </Pressable>
        </View>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.bodyLight,
            fontSize: 13.5,
            lineHeight: 20,
            color: WM,
            maxWidth: 720,
          }}
        >
          Every deliverable for this case in sequence, with submission counts and gate / scoring flags. Click a row
          to view the spec; use the editor to add or reorder.
        </Text>

        {/* Rows */}
        <View
          style={{
            marginTop: 22,
            backgroundColor: OFF_BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <View style={{ padding: 24 }}>
              <Text style={{ color: WM, fontFamily: FONTS.body, fontSize: 13 }}>
                Loading deliverables…
              </Text>
            </View>
          ) : items.length === 0 ? (
            <View style={{ padding: 28 }}>
              <Text style={{ fontFamily: FONTS.display, fontSize: 22, color: W }}>
                No deliverables yet.
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: WM,
                  maxWidth: 540,
                }}
              >
                Open the editor to add your first deliverable — students see them in the order you set here.
              </Text>
            </View>
          ) : (
            items.map((d, i) => {
              const dueMs = d.due_at ? Date.parse(d.due_at) : null;
              const overdue = dueMs != null && dueMs < now && activeCase?.status === "open";
              const dueSoon = dueMs != null && dueMs - now < 1000 * 60 * 60 * 24 * 3 && dueMs >= now;
              const dueColor = overdue ? RED : dueSoon ? AMBER : WM;
              const opensMs = d.opens_at ? Date.parse(d.opens_at) : null;
              const isLockedNow = opensMs != null && opensMs > now;
              const ratio = d.total_team_count > 0 ? d.submitted_team_count / d.total_team_count : 0;
              const ratioColor = ratio >= 0.8 ? SAGE : ratio >= 0.4 ? AMBER : WM;
              return (
                <Pressable
                  key={d.id}
                  onPress={() =>
                    router.push(`/client/competition/${urlCaseId}/deliverable/${d.id}` as Href)
                  }
                  style={[
                    {
                      paddingHorizontal: 18,
                      paddingVertical: 16,
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 16,
                      borderTopWidth: i === 0 ? 0 : 1,
                      borderTopColor: BORDER,
                    } as ViewStyle,
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  {/* Sequence chip */}
                  <View
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 4,
                      backgroundColor: "rgba(181,135,58,0.10)",
                      borderWidth: 1,
                      borderColor: "rgba(181,135,58,0.32)",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.displayMedium,
                        fontSize: 18,
                        color: AMBER,
                      }}
                    >
                      {d.sequence}
                    </Text>
                  </View>

                  {/* Identity */}
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Text
                        numberOfLines={1}
                        style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: W }}
                      >
                        {d.name}
                      </Text>
                      {d.is_locked_gate ? <Pill label="Gate" tint={AMBER} /> : null}
                      {isLockedNow ? <Pill label="Locked" tint={WM} /> : null}
                      {d.is_scored ? <Pill label="Scored" tint={SAGE} /> : null}
                      {!d.allow_revisions ? <Pill label="Final only" tint={WM} /> : null}
                    </View>
                    <Text
                      numberOfLines={1}
                      style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}
                    >
                      {fileTypeLabel(d.file_type)}
                      {d.point_value != null ? ` \u00B7 ${d.point_value} pts` : ""}
                    </Text>
                  </View>

                  {/* Unlocks */}
                  <View style={{ minWidth: 130 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 8.5,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: WL,
                      }}
                    >
                      Unlocks
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={{
                        marginTop: 2,
                        fontFamily: FONTS.bodyMedium,
                        fontSize: 12,
                        color: isLockedNow ? AMBER : WM,
                      }}
                    >
                      {d.opens_at ? formatDeadlineCompact(d.opens_at) : "At comp open"}
                    </Text>
                  </View>

                  {/* Due */}
                  <View style={{ minWidth: 160 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 8.5,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: WL,
                      }}
                    >
                      Due
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={{
                        marginTop: 2,
                        fontFamily: FONTS.bodyMedium,
                        fontSize: 12,
                        color: dueColor,
                      }}
                    >
                      {d.due_at ? formatDeadlineCompact(d.due_at) : "\u2014"}
                    </Text>
                  </View>

                  {/* Submission progress */}
                  <View style={{ minWidth: 130, alignItems: "flex-end" }}>
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 8.5,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: WL,
                      }}
                    >
                      Submissions
                    </Text>
                    <Text
                      style={{
                        marginTop: 2,
                        fontFamily: FONTS.displayMedium,
                        fontSize: 18,
                        color: ratioColor,
                      }}
                    >
                      {d.submitted_team_count}
                      <Text style={{ fontSize: 12, color: WL }}>
                        {" / "}
                        {d.total_team_count}
                      </Text>
                    </Text>
                  </View>
                </Pressable>
              );
            })
          )}
        </View>
    </ScrollView>
  );
}

function Pill({ label, tint }: { label: string; tint: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: tint,
        backgroundColor: "rgba(255,255,255,0.04)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 8,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: tint,
        }}
      >
        {label}
      </Text>
    </View>
  );
}
